/**
 * Compact price formatting for the axis, legend and crosshair.
 *
 * A market cap axis reading `28,530,813` makes you count digits to know
 * whether you are looking at twenty-eight million or two hundred and eighty.
 * Every DEX terminal abbreviates instead — `28.53M` — and the reason is that
 * on a price axis the magnitude is the thing you read first and the precision
 * is the thing you read second.
 *
 * Two decimals are kept at every scale, matching what DexScreener prints
 * (`1.80M`, `897.67K`). Trimming them would make the axis labels jump between
 * widths as the scale moves, which is worse than a redundant zero.
 */

import type { Denom } from './symbol'

/**
 * How a satoshi-denominated series is read: as sats, or as BTC.
 *
 * One chart, one unit. A puppet at 3.3M sats reads as 0.0329 BTC and an ORDI
 * at 7,091 sats reads as sats; a unit that flipped between axis labels as the
 * viewer scrolled would be worse than either. The unit is decided once per
 * symbol from the typical price of its first page of bars (see `units.ts`)
 * and held for the life of that symbol.
 */
export type PriceUnit = 'sats' | 'btc'

/** Typical price at or above which a series is read in BTC: 0.001 BTC. */
export const BTC_UNIT_THRESHOLD_SATS = 100_000

export function unitForSats(typicalSats: number): PriceUnit {
	return Number.isFinite(typicalSats) && typicalSats >= BTC_UNIT_THRESHOLD_SATS ? 'btc' : 'sats'
}

/**
 * A satoshi amount as BTC, clean at every magnitude: four significant digits
 * below one bitcoin, three decimals above, one above a hundred. Never
 * exponent notation, never trailing zeros beyond the fixed width.
 */
export function formatBtc(sats: number): string {
	if (!Number.isFinite(sats)) {
		return ''
	}
	const btc = sats / 1e8
	const abs = Math.abs(btc)
	if (abs === 0) {
		return '0.0000'
	}
	if (abs >= 100) {
		return btc.toFixed(1)
	}
	if (abs >= 1) {
		return btc.toFixed(3)
	}
	const fixed = abs.toPrecision(4)
	const plain = fixed.includes('e') ? abs.toFixed(10).replace(/0+$/, '') : fixed
	return `${btc < 0 ? '-' : ''}${plain}`
}

const UNITS: { limit: number; suffix: string }[] = [
	{ limit: 1e12, suffix: 'T' },
	{ limit: 1e9, suffix: 'B' },
	{ limit: 1e6, suffix: 'M' },
	{ limit: 1e3, suffix: 'K' }
]

/**
 * Below this, two decimals would round a real price to `0.00`.
 *
 * Alkanes routinely trade at fractions of a cent, so a formatter that always
 * prints two decimals turns a whole axis into zeros. Under this threshold the
 * value is shown with enough significant digits to stay distinguishable.
 */
const SMALL_VALUE_FLOOR = 0.01

/** Significant digits kept for values below the floor. */
const SMALL_VALUE_SIGNIFICANT = 4

/** Below this a value has at least four zeros after the point and is written
 * with a subscript zero count instead. */
const SUBSCRIPT_BELOW = 1e-4

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉'

function subscript(n: number): string {
	return String(n)
		.split('')
		.map((c) => SUBSCRIPT_DIGITS[Number(c)] ?? c)
		.join('')
}

/**
 * `0.00007953` -> `0.0₄7953`: the count of zeros after the point as a
 * subscript, then four significant digits with trailing zeros dropped.
 * `abs` must be positive and below `SUBSCRIPT_BELOW`.
 */
export function subscriptZeros(abs: number): string {
	let exp = Math.floor(Math.log10(abs))
	let digits = Math.round(abs / 10 ** exp * 1000)
	if (digits >= 10_000) {
		// 9.9996e-5 rounds up to the next magnitude.
		exp += 1
		digits = 1000
	}
	const zeros = -exp - 1
	const sig = String(digits).replace(/0+$/, '')
	return `0.0${subscript(zeros)}${sig}`
}

/**
 * Format a number the way a DEX terminal does.
 *
 * `compactPrice(28_530_813)` -> `28.53M`
 * `compactPrice(897_670)`    -> `897.67K`
 * `compactPrice(0.0000054)`  -> `0.0000054`
 */
export function compactPrice(value: number): string {
	if (!Number.isFinite(value)) {
		return ''
	}

	const sign = value < 0 ? '-' : ''
	const abs = Math.abs(value)

	if (abs === 0) {
		return '0.00'
	}

	for (const { limit, suffix } of UNITS) {
		if (abs >= limit) {
			return `${sign}${(abs / limit).toFixed(2)}${suffix}`
		}
	}

	// Tiny: a TAP token at 0.00007953 sats or $0.000000066934 is a run of
	// zeros nobody can count. DEX terminals fold the zeros into a subscript
	// -- `0.0₄7953` -- and so does this, once there are four or more.
	if (abs < SUBSCRIPT_BELOW) {
		return `${sign}${subscriptZeros(abs)}`
	}

	// Small but non-zero: two decimals would print 0.00 and lose the number
	// entirely, so keep significant digits instead. `toPrecision` can hand back
	// exponent notation, which is not what anyone wants on a price axis.
	if (abs < SMALL_VALUE_FLOOR) {
		const fixed = abs.toPrecision(SMALL_VALUE_SIGNIFICANT)
		const plain = fixed.includes('e') ? abs.toFixed(12) : fixed
		// Drop trailing zeros, but never leave a bare decimal point behind.
		return `${sign}${plain.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.$/, '')}`
	}

	return `${sign}${abs.toFixed(2)}`
}

/**
 * The formatter for a given denomination.
 *
 * Satoshis are a COUNT, not a currency, so they get no dollar sign. Market cap
 * is always large enough to be worth abbreviating. A dollar price is only
 * abbreviated once it is over a thousand — `$4.20` should not become `4.20`,
 * and a token trading under a cent needs its digits rather than a suffix.
 */
export function formatForDenom(value: number, denom: Denom, unit: PriceUnit = 'sats'): string {
	if (!Number.isFinite(value)) {
		return ''
	}

	if (denom === 'sats' && unit === 'btc') {
		return formatBtc(value)
	}

	if (denom === 'mcap') {
		return compactPrice(value)
	}

	if (denom === 'sats') {
		// Large sat values benefit from the same treatment; whole-sat values
		// are counts and should look like counts. Below one sat -- a TAP or
		// BRC-20 unit price such as DMT-NAT's 0.000092 -- rounding to a count
		// prints "0" on every axis label and in the legend, so keep the
		// significant digits.
		const abs = Math.abs(value)
		if (abs >= 1e3 || abs < 1) {
			return compactPrice(value)
		}
		return String(Math.round(value))
	}

	return compactPrice(value)
}
